import React, {useContext} from 'react';
import {Audit} from "@bdfx/audit-api/types"
import {
    css,
    Grid,
    Icon,
    Button,
    Div,
} from "@bdfx/fukit"
import {formatInt, formatPNO } from "../../common/format"
import Chart from "chart.js"
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {eventGA} from "../../common/tracking"

import history from "../../common/history"
import Results from "./results"
import ResultHeader from "./result-header"
import Categories from "./categories"
import Competitors from "./competitors"
import Pies from "./pies"
import Footer from "./footer"
import Positions from "../result-detail/positions"
import ctrans from "../../common/trans"
import trans from "../trans"
import {AuditContext} from "../providers/AuditProvider"
import {Placeholder} from "../app"
import {getColor, ERRORS, WARNINGS, SUGGESTIONS, getProblemUrl, }  from "../helpers"
import {getAuditId} from "../../common/helpers"
import Wave from "../homepage/wave"
import ErrorPage from "../providers/error-page"
Chart.pluginService.register(ChartDataLabels);

export const getIcon = (index) => {
    let group = index
    if(ERRORS.indexOf(index) > -1) {
        group = "bidding_group_problems"
    } else if(WARNINGS.indexOf(index) > -1) {
        group = "bidding_group_warnings"
    } else if(SUGGESTIONS.indexOf(index) > -1) {
        group = "bidding_group_recommendations"
    }

    switch(group) {
        case "bidding_group_ok": 
            return "cup-light"
        case "bidding_group_problems": 
            return "spam-light"
        case "bidding_group_warnings": 
            return "danger-light"
        case "bidding_group_recommendations": 
        default:
            return "info-light"
    }
}
function Result() {
    const [data]: [Audit] = useContext(AuditContext)

    if(getAuditId() === "example") {
        document.title = ctrans("example_results") + " | Bidding Audit"
    } else {
        if(data?.shop?.name)
        document.title = ctrans("audit_result") + ": " + data.shop.name + " | Bidding Audit"
    }

    if(!data) {
        return <Placeholder /> 
    }

    const { status, result, shop } = data
    
    if(status === "working" && !result) {
        eventGA("AUDIT", "Audit generating", "AUDIT_IN_PROGRESS")

        return <ErrorPage code={400} />
    }

    if(getAuditId() !== "example") {
        eventGA("AUDIT", "Audit detail", "AUDIT_DETAIL")
    }

    const errors = ERRORS.reduce((acc,index) => ({...acc, [index]: result?.[index]}) ,{})
    const warnings = WARNINGS.reduce((acc,index) => ({...acc, [index]: result?.[index]}) ,{})
    const suggestions = SUGGESTIONS.reduce((acc,index) => ({...acc, [index]: result?.[index]}) ,{})
    const errorsCount = result?.bidding_group_problems
    const warningsCount = result?.bidding_group_warnings
    const suggestionsCount = result?.bidding_group_recommendations
    const okCount = result?.bidding_group_ok
    
    const goToDetail = (index) => (e) => { 
        e.preventDefault()
        history.push(getProblemUrl(data.id, index))
        window.scrollTo(0,0)
        e.stopPropagation()
        return false
    }

    const total = result?.total_products

    return (
    <Style>
        <ResultHeader data={data}>
            <p>
                {trans("audit_result_info", {
                    total: formatInt(result?.total_products),
                    paired: formatInt(result?.paired),
                    most_position: formatInt(result?.price_positions_stats?.mode),
                    avg_offers: formatInt(result?.shops_count_stats?.avg),

                })}
                <small style={{ marginTop: "25px", float: "right"}}><em>{trans("audit_result_paired_hint")}</em></small>
                <Div clear="both" />

            </p>
            <Grid className="flags" cols="1fr 1fr 1fr 1fr" gap="20px">
                    <SummaryBox index={"bidding_group_ok"}>
                        <span className="products-total">
                            {formatPNO(okCount * 100 / total)}
                        </span>
                        <br/>
                        {ctrans("n_products", {n: -1})}
                        <br/>
                        <Icon color="white" size="35" type={getIcon("bidding_group_ok")} />
                        <br/>
                        <strong>{trans("bidding_group_ok").toLowerCase()}</strong>
                        <br/>
                        <Button 
                                as="a" href={getProblemUrl(data.id, 'bidding_group_ok')} 
                                onClick={goToDetail('bidding_group_ok')}
                                secondary={true} color="white" size="small" 
                                icon="chevron-right-light" style={{ marginTop: "10px",}}>{ctrans("detail")}</Button>
                    </SummaryBox>

                    <SummaryBox index={"bidding_group_problems"}>
                        <span className="products-total">
                            {formatInt(errorsCount)}
                        </span>
                        <br/>
                        {ctrans("n_products", {n: -1})}
                        <br/>
                        <Icon color="white" size="35" type={getIcon("bidding_group_problems")} />
                        <br/>
                        <strong>{trans("bidding_group_problems").toLowerCase()}</strong>
                        <br/>
                        <Button as="a" href={getProblemUrl(data.id, 'bidding_group_problems')} onClick={goToDetail('bidding_group_problems')} secondary={true} color="white" size="small" icon="chevron-right-light" style={{ marginTop: "10px",}}>{ctrans("detail")}</Button>
                    </SummaryBox>


                    <SummaryBox index={"bidding_group_warnings"}>
                        <span className="products-total">
                            {formatInt(warningsCount)}
                        </span>
                        <br/>
                        {ctrans("n_products", {n: -1})}
                        <br/>
                        <Icon color="white" size="35" type={getIcon("bidding_group_warnings")} />
                        <br/>
                        <strong>{trans("bidding_group_warnings").toLowerCase()}</strong>
                        <br/>
                        <Button as="a" href={getProblemUrl(data.id, 'bidding_group_warnings')} 
                                onClick={goToDetail('bidding_group_warnings')}
                                secondary={true} color="white" size="small" icon="chevron-right-light" 
                                style={{ marginTop: "10px",}}>{ctrans("detail")}</Button>
                    </SummaryBox>


                    <SummaryBox index={"bidding_group_recommendations"}>
                        <span className="products-total">
                            {formatInt(suggestionsCount)}
                        </span>
                        <br/>
                        {ctrans("n_products", {n: -1})}
                        <br/>
                        <Icon color="white" size="35" type={getIcon("bidding_group_recommendations")} />
                        <br/>
                        <strong>{trans("bidding_group_recommendations").toLowerCase()}</strong>
                        <br/>
                        <Button as="a" href={getProblemUrl(data.id, 'bidding_group_recommendations')} 
                                onClick={goToDetail('bidding_group_recommendations')}
                                secondary={true} color="white" size="small" icon="chevron-right-light" 
                                style={{ marginTop: "10px",}}>{ctrans("detail")}</Button>
                    </SummaryBox>
                </Grid>
        </ResultHeader>

        <Pies data={result} />        
        
        <Wave from="white" to="#ffeced" v={1} />
            
        <Positions total={result?.paired} locale={shop?.heureka_locale} data={result?.positions_by_prices} />
        <Wave from="#ffeced" to="white" v={2} />

        <Categories top_categories={result?.top_categories} />
        <Wave from="white" to="#ffeced" v={1} />

        <Competitors competitors={result?.competitors_bidding} />
        <Wave from="#ffeced" to="white" v={2} />
        <div className="pbreak" />
        <Results  deleted={data.status === "deleted"} auditId={data.id} errors={errors} warnings={warnings} suggestions={suggestions} total={result?.total_products} />
        <Wave from="white" to="#ffeced" v={4} />
        <Footer />
        <Wave from="#ffeced" to="#d32c30" v={3} />
    </Style>
  );
}
 

export const Style = css("div")({
    ".flags": {
        margin: "auto",
    },

    "p": {
        padding: "0 1em !important",
    },

    "@media only screen and (max-width: 1000px)": {
        ".flags": {
            display: "block",
            " > div": {
                display: "inline-block",
                margin: "5px",
            },
        },
    },

})

export const SummaryBox = css("div")({
    padding: "30px",
    fontSize: "1.4em",
    width: "260px",
    height: "260px",
    boxShadow: "3px 5px 5px rgba(0,0,0,0.1)",
    lineHeight: "1.4em",
    color: "white",
    paddingBottom: "60px",
    borderRadius: "999em",
    "> .fuk-icon": {
        marginTop: "10px",
        marginBottom: "10px",
    },
    ".fuk-button": {
        fontSize: "1em",
        display: "inline-block",
        padding: "6px 20px !important",
    },
    ".products-total": {
        fontSize: "1.3em !important",
        fontWeight: "bold",
    },

}, props => ({
    background: getColor(props.index),
}))



export default Result;
